import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../lib/api.js";
import {
  createHousingAssessment,
  updateHousingAssessment,
} from "../../lib/api-modules/housing-assessments.js";
import {
  ASSESSMENT_PAYMENT_SCHEDULE_LABELS,
  ASSESSMENT_TYPE_LABELS,
  type AssessmentPaymentSchedule,
  type AssessmentType,
  type CreateHousingAssessmentInput,
  type HousingAssessment,
} from "../../lib/api-modules/types.js";
import { ErrorMessage } from "../auth/error-message.js";

const TYPE_OPTIONS: ReadonlyArray<{ value: AssessmentType; label: string }> = (
  Object.keys(ASSESSMENT_TYPE_LABELS) as AssessmentType[]
).map((v) => ({ value: v, label: ASSESSMENT_TYPE_LABELS[v] }));

const SCHEDULE_OPTIONS: ReadonlyArray<{
  value: AssessmentPaymentSchedule;
  label: string;
}> = (
  Object.keys(ASSESSMENT_PAYMENT_SCHEDULE_LABELS) as AssessmentPaymentSchedule[]
).map((v) => ({ value: v, label: ASSESSMENT_PAYMENT_SCHEDULE_LABELS[v] }));

interface HousingAssessmentFormProps {
  readonly mode: "create" | "edit";
  readonly unitId: string;
  readonly assessment?: HousingAssessment;
  readonly onClose: () => void;
}

export function HousingAssessmentForm({
  mode,
  unitId,
  assessment,
  onClose,
}: HousingAssessmentFormProps) {
  const queryClient = useQueryClient();
  const isEdit = mode === "edit";

  const [type, setType] = useState<AssessmentType>(
    assessment?.assessment_type ?? "facade_law_11",
  );
  const [totalDollars, setTotalDollars] = useState<string>(
    assessment ? (assessment.total_amount_cents / 100).toString() : "",
  );
  const [perShareCents, setPerShareCents] = useState<string>(
    assessment?.per_share_cents !== undefined
      ? (assessment.per_share_cents / 100).toString()
      : "",
  );
  const [startDate, setStartDate] = useState<string>(
    assessment?.start_date ?? new Date().toISOString().slice(0, 10),
  );
  const [endDate, setEndDate] = useState<string>(assessment?.end_date ?? "");
  const [schedule, setSchedule] = useState<AssessmentPaymentSchedule>(
    assessment?.payment_schedule ?? "monthly",
  );
  const [remainingDollars, setRemainingDollars] = useState<string>(
    assessment?.remaining_cents !== undefined
      ? (assessment.remaining_cents / 100).toString()
      : "",
  );
  const [isCapitalizable, setIsCapitalizable] = useState<boolean>(
    assessment?.is_capitalizable_to_basis ?? false,
  );
  const [notes, setNotes] = useState<string>(assessment?.notes ?? "");
  const [inlineError, setInlineError] = useState<string | null>(null);

  const isFacadeLaw11 = type === "facade_law_11";

  const mutation = useMutation({
    mutationFn: async () => {
      const total = parseDollars(totalDollars);
      if (total === undefined)
        throw new Error("Total amount is required (in dollars)");
      if (!startDate) throw new Error("Start date is required");

      const baseInput: CreateHousingAssessmentInput = {
        assessment_type: type,
        total_amount_cents: total,
        start_date: startDate,
        payment_schedule: schedule,
        is_capitalizable_to_basis: isCapitalizable,
      };
      const ps = parseDollars(perShareCents);
      if (ps !== undefined) baseInput.per_share_cents = ps;
      if (endDate) baseInput.end_date = endDate;
      const rem = parseDollars(remainingDollars);
      if (rem !== undefined) baseInput.remaining_cents = rem;
      if (notes.trim()) baseInput.notes = notes.trim();

      if (isEdit && assessment) {
        return updateHousingAssessment(unitId, assessment.assessment_id, baseInput);
      }
      return createHousingAssessment(unitId, baseInput);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["housing-assessments", unitId],
      });
      onClose();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : err instanceof Error
            ? err.message
            : "Couldn't save assessment.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      aria-label={isEdit ? "Edit assessment" : "Add assessment"}
      className="flex flex-col gap-3 rounded-md border border-zinc-200 bg-zinc-50/40 p-3 dark:border-zinc-800 dark:bg-zinc-900/40"
    >
      <header className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">
          {isEdit ? "Edit assessment" : "Add assessment"}
        </h4>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-zinc-500 hover:underline"
          disabled={mutation.isPending}
        >
          Cancel
        </button>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium">Type</label>
          <select
            className={inputClass}
            value={type}
            onChange={(e) => setType(e.target.value as AssessmentType)}
            disabled={mutation.isPending}
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {isFacadeLaw11 && (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
              FISP runs on a 5-year cycle in NYC; assessments often span
              multiple years.
            </p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Total amount ($){" "}
            <span className="text-zinc-500">(building-level)</span>
          </label>
          <input
            type="number"
            step="0.01"
            className={inputClass}
            value={totalDollars}
            onChange={(e) => setTotalDollars(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Per share ($){" "}
            <span className="text-zinc-500">(co-op only)</span>
          </label>
          <input
            type="number"
            step="0.01"
            className={inputClass}
            value={perShareCents}
            onChange={(e) => setPerShareCents(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Payment schedule
          </label>
          <select
            className={inputClass}
            value={schedule}
            onChange={(e) =>
              setSchedule(e.target.value as AssessmentPaymentSchedule)
            }
            disabled={mutation.isPending}
          >
            {SCHEDULE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Start date</label>
          <input
            type="date"
            className={inputClass}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            End date <span className="text-zinc-500">(optional)</span>
          </label>
          <input
            type="date"
            className={inputClass}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Remaining ($) <span className="text-zinc-500">(optional)</span>
          </label>
          <input
            type="number"
            step="0.01"
            className={inputClass}
            value={remainingDollars}
            onChange={(e) => setRemainingDollars(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div className="flex items-end pb-1.5">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={isCapitalizable}
              onChange={(e) => setIsCapitalizable(e.target.checked)}
              disabled={mutation.isPending}
            />
            Capital improvement (adds to cost basis at sale)
          </label>
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium">
          Notes <span className="text-zinc-500">(optional)</span>
        </label>
        <textarea
          className={inputClass}
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={mutation.isPending}
          maxLength={2000}
        />
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending
            ? "Saving…"
            : isEdit
              ? "Save assessment"
              : "Add assessment"}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-1.5 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
        >
          Cancel
        </button>
      </div>

      {inlineError && <ErrorMessage>{inlineError}</ErrorMessage>}
    </form>
  );
}

const inputClass =
  "w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900 disabled:cursor-not-allowed disabled:opacity-60";

function parseDollars(s: string): number | undefined {
  const trimmed = s.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
}
